import React from "react";
import { Controller } from "react-hook-form";
import PhoneInput from "react-phone-number-input";
import { isValidPhoneNumber } from "react-phone-number-input/mobile";
import validator from "validator";

import { ReactComponent as CloseIcon } from "../../assets/images/cross.svg";
import { emailConflict, phoneConflict } from "../../utils/tableRequest";

const GENDERS = [
  { value: "m", label: "Male" },
  { value: "f", label: "Female" },
];

const FieldError = ({ id, error }) =>
  error ? (
    <span className="table-request-form__field-error" id={id}>
      {error.message}
    </span>
  ) : null;

const TableRequestGuestFields = ({
  index,
  register,
  control,
  errors = {},
  gender,
  onRemove,
  getValues = () => [],
  ownEmail = "",
  ownPhone = "",
}) => {
  // The requester is Guest 1, so invited guests start at 2.
  const number = index + 2;
  const others = (field) => (getValues("guests") || []).map((guest) => guest?.[field]);
  const name = (field) => `guests.${index}.${field}`;
  const fieldId = (field) => `guest-${index}-${field}`;
  const errorId = (field) => `${fieldId(field)}-error`;
  const describedBy = (field) => (errors[field] ? errorId(field) : undefined);
  const inputClass = (field) =>
    `registration-form__input table-request-form__input${
      errors[field] ? " table-request-form__input--error" : ""
    }`;

  return (
    <fieldset className="table-request-form__guest">
      <legend className="table-request-form__guest-title">Guest {number}</legend>
      {onRemove ? (
        <button
          type="button"
          className="table-request-form__remove"
          onClick={onRemove}
          aria-label={`Remove guest ${number}`}
        >
          <CloseIcon aria-hidden="true" />
        </button>
      ) : null}

      <div className="table-request-form__grid">
        <div className="table-request-form__field">
          <label className="table-request-form__label" htmlFor={fieldId("firstName")}>
            First name
          </label>
          <input
            id={fieldId("firstName")}
            className={inputClass("firstName")}
            autoComplete="off"
            aria-invalid={errors.firstName ? "true" : "false"}
            aria-describedby={describedBy("firstName")}
            {...register(name("firstName"), {
              required: "First name is required",
            })}
          />
          <FieldError id={errorId("firstName")} error={errors.firstName} />
        </div>

        <div className="table-request-form__field">
          <label className="table-request-form__label" htmlFor={fieldId("lastName")}>
            Last name
          </label>
          <input
            id={fieldId("lastName")}
            className={inputClass("lastName")}
            autoComplete="off"
            aria-invalid={errors.lastName ? "true" : "false"}
            aria-describedby={describedBy("lastName")}
            {...register(name("lastName"), {
              required: "Last name is required",
            })}
          />
          <FieldError id={errorId("lastName")} error={errors.lastName} />
        </div>

        <div className="table-request-form__field table-request-form__field--full">
          <label className="table-request-form__label" htmlFor={fieldId("email")}>
            Email
          </label>
          <input
            id={fieldId("email")}
            type="email"
            inputMode="email"
            autoComplete="off"
            className={inputClass("email")}
            aria-invalid={errors.email ? "true" : "false"}
            aria-describedby={describedBy("email")}
            {...register(name("email"), {
              required: "Email is required",
              validate: (value) => {
                if (!validator.isEmail((value || "").trim())) {
                  return "That email address doesn't look right.";
                }
                return emailConflict(value, index, others("email"), ownEmail) || true;
              },
            })}
          />
          <FieldError id={errorId("email")} error={errors.email} />
        </div>

        <div className="table-request-form__field">
          <label
            className="table-request-form__label"
            htmlFor={fieldId("mobileNumber")}
          >
            Mobile number
          </label>
          <Controller
            control={control}
            name={name("mobileNumber")}
            rules={{
              required: "Mobile number is required",
              validate: (value) => {
                if (!isValidPhoneNumber(value || "")) {
                  return "That mobile number doesn't look right.";
                }
                return phoneConflict(value, index, others("mobileNumber"), ownPhone) || true;
              },
            }}
            render={({ field }) => (
              <PhoneInput
                ref={field.ref}
                id={fieldId("mobileNumber")}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                defaultCountry="EG"
                className={errors.mobileNumber ? "PhoneInput--error" : ""}
                aria-invalid={errors.mobileNumber ? "true" : "false"}
                aria-describedby={describedBy("mobileNumber")}
              />
            )}
          />
          <FieldError id={errorId("mobileNumber")} error={errors.mobileNumber} />
        </div>

        <fieldset className="table-request-form__field table-request-form__fieldset">
          <legend className="table-request-form__label" id={fieldId("gender")}>
            Gender
          </legend>
          <div
            className="table-request-form__segments"
            role="radiogroup"
            aria-labelledby={fieldId("gender")}
            aria-invalid={errors.gender ? "true" : "false"}
            aria-describedby={describedBy("gender")}
          >
            {GENDERS.map((option) => (
              <label
                key={option.value}
                className={`table-request-form__segment${
                  gender === option.value
                    ? " table-request-form__segment--selected"
                    : ""
                }${errors.gender ? " table-request-form__segment--error" : ""}`}
              >
                <input
                  type="radio"
                  value={option.value}
                  {...register(name("gender"), {
                    required: "Gender is required",
                  })}
                />
                {option.label}
              </label>
            ))}
          </div>
          <FieldError id={errorId("gender")} error={errors.gender} />
        </fieldset>
      </div>
    </fieldset>
  );
};

export default TableRequestGuestFields;
